import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, Form, DatePicker, Select, Input, InputNumber, message, Popconfirm, Card, Tag, Checkbox, Tabs, Alert, Radio, TimePicker, Switch, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SettingOutlined, UserAddOutlined, CalendarOutlined, AppstoreOutlined, ClockCircleOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import DutyAreaSettings from './DutyAreaSettings';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const cloneWeeklySchedule = (schedule) => Object.fromEntries(
  Object.entries(schedule).map(([day, students]) => [day, students.map(student => ({ ...student }))])
);

const normalizeDutyWeekday = (value) => {
  const numericValue = Number(value);
  if (Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7) {
    return numericValue;
  }

  const normalized = String(value || '').trim().replace(/^星期/, '周');
  const weekdayMap = {
    '周一': 1,
    '周二': 2,
    '周三': 3,
    '周四': 4,
    '周五': 5,
    '周六': 6,
    '周日': 7,
    '周天': 7
  };
  return weekdayMap[normalized] || null;
};

const DutyStudentManagement = ({ classId, classInfo }) => {
  const [dutyStudents, setDutyStudents] = useState([]);
  const [classStudents, setClassStudents] = useState([]); // 班级学生列表
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [weeklyScheduleModalVisible, setWeeklyScheduleModalVisible] = useState(false); // 周轮值配置模态框
  const [autoArrangeModalVisible, setAutoArrangeModalVisible] = useState(false); // 一键生成模态框
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false); // 导入模态框
  const [importing, setImporting] = useState(false); // 导入中状态
  const [customAreas, setCustomAreas] = useState([]); // 自定义区域列表
  const [innerTab, setInnerTab] = useState('list'); // 内部Tab: list(值日生列表) 或 areas(区域设置)
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentAreas, setStudentAreas] = useState({}); // 学生对应的值日区域
  const [weeklySchedule, setWeeklySchedule] = useState({}); // 周轮值表: { 1: [{studentId, studentName, area}], 2: [...], ... }
  const [weeklyDraft, setWeeklyDraft] = useState({});
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [editingWeekly, setEditingWeekly] = useState(null);
  const [weeklyEditForm] = Form.useForm();
  const weeklySaveInFlight = useRef(false);
  const weeklyFetchVersion = useRef(0);
  const currentClassId = useRef(classId);
  currentClassId.current = classId;
  const [displayConfig, setDisplayConfig] = useState(null);
  const [viewMode, setViewMode] = useState('weekly'); // 'date' 或 'weekly' - 默认改为weekly
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();
  const [autoArrangeForm] = Form.useForm();
  
  // 打扫时间设置相关状态
  const [cleaningTemplates, setCleaningTemplates] = useState([]);
  const [cleaningSchedules, setCleaningSchedules] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [cleaningModalVisible, setCleaningModalVisible] = useState(false);
  const [editingCleaning, setEditingCleaning] = useState(null);
  const [cleaningForm] = Form.useForm();
  const [schoolSchedules, setSchoolSchedules] = useState([]); // 作息时间表
  const weeklyBusy = weeklySaving || weeklyLoading;

  // 常用值日区域选项（仅作为建议，用户可自定义）
  const dutyAreaSuggestions = [
    '扫地（1-2组）',
    '扫地（3-4组）',
    '扫地（5-6组）',
    '拖地（教室前部）',
    '拖地（教室后部）',
    '擦黑板',
    '擦窗户',
    '整理讲台',
    '倒垃圾',
    '摆放桌椅',
    '清理走廊',
    '清理卫生区'
  ];

  // 获取可用的区域列表（优先使用自定义区域）
  const getAvailableAreas = () => {
    if (customAreas.length > 0) {
      return customAreas.map(area => ({
        value: area.area_name,
        label: area.area_name,
        description: area.area_description
      }));
    }
    return dutyAreaSuggestions.map(area => ({
      value: area,
      label: area,
      description: null
    }));
  };

  // 星期选项
  const weekDays = [
    { value: 1, label: '星期一' },
    { value: 2, label: '星期二' },
    { value: 3, label: '星期三' },
    { value: 4, label: '星期四' },
    { value: 5, label: '星期五' },
    { value: 6, label: '星期六' },
    { value: 7, label: '星期日' }
  ];

  useEffect(() => {
    weeklyFetchVersion.current += 1;
    setWeeklySchedule({});
    setWeeklyDraft({});
    setWeeklyScheduleModalVisible(false);
    setEditingWeekly(null);
    if (classId) {
      fetchDutyStudents();
      fetchClassStudents();
      fetchDisplayConfig();
      fetchWeeklySchedule(); // 获取周轮值表
      fetchCustomAreas(); // 获取自定义区域
      fetchSchoolSchedules(); // 获取作息时间表
      fetchCleaningTemplates(); // 获取打扫时间模板
      fetchCleaningSchedules(); // 获取班级打扫时间安排
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // 获取值日生列表
  const fetchDutyStudents = async (startDate, endDate) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { classId };
      
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await axios.get('/api/v2/duty-students', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDutyStudents(response.data.data);
      }
    } catch (error) {
      console.error('获取值日生列表失败:', error);
      message.error('获取值日生列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取班级学生列表
  const fetchClassStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/students/class/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setClassStudents(response.data.data);
      }
    } catch (error) {
      console.error('获取班级学生列表失败:', error);
      message.error('获取班级学生列表失败');
    }
  };

  // 获取展示配置
  const fetchDisplayConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/duty-students/display-config/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDisplayConfig(response.data.data);
      }
    } catch (error) {
      console.error('获取展示配置失败:', error);
    }
  };

  // 获取周轮值表
  const fetchWeeklySchedule = async () => {
    const fetchVersion = ++weeklyFetchVersion.current;
    const requestedClassId = classId;
    setWeeklyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/duty-students/weekly-schedule/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (currentClassId.current !== requestedClassId || weeklyFetchVersion.current !== fetchVersion) return;
      if (response.data.success && response.data.data) {
        // 将数据库格式转换为前端格式
        const schedule = {};
        response.data.data.forEach(item => {
          if (!schedule[item.day_of_week]) {
            schedule[item.day_of_week] = [];
          }
          schedule[item.day_of_week].push({
            studentId: item.student_id,
            studentName: item.student_name,
            area: item.duty_area
          });
        });
        setWeeklySchedule(schedule);
      }
    } catch (error) {
      console.error('获取周轮值表失败:', error);
      // 接口可能不存在，忽略错误
    } finally {
      if (currentClassId.current === requestedClassId && weeklyFetchVersion.current === fetchVersion) {
        setWeeklyLoading(false);
      }
    }
  };

  // 获取自定义区域列表
  const fetchCustomAreas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/duty-areas/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCustomAreas(response.data.data);
      }
    } catch (error) {
      console.error('获取自定义区域失败:', error);
      // 如果没有配置区域，不显示错误
    }
  };

  // ==================== 打扫时间管理 ====================
  
  // 获取作息时间表
  const fetchSchoolSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/schedules/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📅 获取作息时间表响应:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('📅 作息时间表数据:', response.data.data);
        setSchoolSchedules(response.data.data);
      } else {
        console.warn('⚠️ 作息时间表数据为空');
      }
    } catch (error) {
      console.error('❌ 获取作息时间表失败:', error);
    }
  };
  
  // 根据参考类型获取实际时间
  const getReferenceTime = (referenceType) => {
    console.log('🔍 getReferenceTime 被调用:', referenceType, 'schoolSchedules长度:', schoolSchedules?.length);
    
    if (!schoolSchedules || schoolSchedules.length === 0) {
      return '作息时间未设置';
    }
    
    console.log('📋 作息时间表内容:', schoolSchedules);
    
    if (referenceType === 'morning_reading') {
      // 先找早读
      let morningReading = schoolSchedules.find(s => 
        s.period_name && (s.period_name.includes('早读') || s.period_name === '早读')
      );
      
      console.log('🌅 查找早读结果:', morningReading);
      
      // 如果没有早读，找上午第一节课（8:00-12:00之间，且是上课类型）
      if (!morningReading) {
        morningReading = schoolSchedules.find(s => {
          if (!s.start_time) return false;
          const hour = parseInt(s.start_time.substring(0, 2));
          return hour >= 6 && hour < 12 && s.period_name && 
                 (s.period_name.includes('第') || s.period_name.includes('节'));
        });
        console.log('🌅 查找上午第一节课结果:', morningReading);
      }
      
      return morningReading ? moment(morningReading.start_time, 'HH:mm:ss').format('HH:mm') : '未设置';
      
    } else if (referenceType === 'noon_reading') {
      // 先找午读
      let noonReading = schoolSchedules.find(s => 
        s.period_name && (s.period_name.includes('午读') || s.period_name === '午读')
      );
      
      // 如果没有午读，找下午第一节课（12:00-18:00之间）
      if (!noonReading) {
        noonReading = schoolSchedules.find(s => {
          if (!s.start_time) return false;
          const hour = parseInt(s.start_time.substring(0, 2));
          return hour >= 12 && hour < 18 && s.period_name && 
                 (s.period_name.includes('第') || s.period_name.includes('节'));
        });
      }
      
      return noonReading ? moment(noonReading.start_time, 'HH:mm:ss').format('HH:mm') : '未设置';
      
    } else if (referenceType === 'afternoon_class') {
      // 找下午第一节课（12:00-18:00之间）
      const afternoonClass = schoolSchedules.find(s => {
        if (!s.start_time) return false;
        const hour = parseInt(s.start_time.substring(0, 2));
        return hour >= 12 && hour < 18 && s.period_name && 
               (s.period_name.includes('第') || s.period_name.includes('节'));
      });
      return afternoonClass ? moment(afternoonClass.start_time, 'HH:mm:ss').format('HH:mm') : '未设置';
      
    } else if (referenceType === 'after_school') {
      // 找最后一节课的结束时间
      const classes = schoolSchedules.filter(s => 
        s.period_name && (s.period_name.includes('第') || s.period_name.includes('节'))
      );
      if (classes.length > 0) {
        const lastClass = classes[classes.length - 1];
        return moment(lastClass.end_time, 'HH:mm:ss').format('HH:mm');
      }
      return '未设置';
    }
    
    return '未知类型';
  };
  
  // 获取打扫时间模板
  const fetchCleaningTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/duty-students/cleaning-templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCleaningTemplates(response.data.data);
      }
    } catch (error) {
      console.error('获取打扫时间模板失败:', error);
    }
  };

  // 获取班级打扫时间安排
  const fetchCleaningSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/duty-students/cleaning-schedule/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCleaningSchedules(response.data.data);
      }
    } catch (error) {
      console.error('获取打扫时间安排失败:', error);
    }
  };

  // 应用模板
  const handleApplyTemplate = async () => {
    if (!selectedTemplate) {
      message.warning('请选择一个模板');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const template = cleaningTemplates.find(t => t.id === selectedTemplate);
      
      await axios.post(`/api/v2/duty-students/cleaning-schedule/${classId}`, {
        schedules: template.schedules
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('模板应用成功');
      fetchCleaningSchedules();
      setSelectedTemplate(null);
    } catch (error) {
      console.error('应用模板失败:', error);
      message.error('应用模板失败');
    }
  };

  // 打开添加/编辑打扫时间对话框
  const openCleaningModal = (schedule = null) => {
    if (schedule) {
      setEditingCleaning(schedule);
      cleaningForm.setFieldsValue({
        name: schedule.schedule_name,
        type: schedule.reference_type,
        time: schedule.reference_time ? moment(schedule.reference_time, 'HH:mm:ss') : null,
        minutesBefore: schedule.minutes_before,
        displayDuration: schedule.display_duration,
        isEnabled: schedule.is_enabled === 1
      });
    } else {
      setEditingCleaning(null);
      cleaningForm.resetFields();
    }
    setCleaningModalVisible(true);
  };

  // 保存打扫时间
  const handleSaveCleaning = async () => {
    try {
      const values = await cleaningForm.validateFields();
      const token = localStorage.getItem('token');
      
      const newSchedule = {
        name: values.name,
        type: values.type,
        time: values.type === 'fixed_time' ? values.time.format('HH:mm:ss') : null,
        minutesBefore: values.minutesBefore,
        displayDuration: values.displayDuration || 120,
        isEnabled: values.isEnabled ? 1 : 0,
        sortOrder: editingCleaning ? editingCleaning.sort_order : cleaningSchedules.length
      };

      if (editingCleaning) {
        // 更新：先删除再添加（简化方案）
        await axios.delete(`/api/v2/duty-students/cleaning-schedule/${editingCleaning.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // 重新保存所有
      const allSchedules = editingCleaning 
        ? cleaningSchedules.filter(s => s.id !== editingCleaning.id).concat(newSchedule)
        : [...cleaningSchedules, newSchedule];

      await axios.post(`/api/v2/duty-students/cleaning-schedule/${classId}`, {
        schedules: allSchedules.map((s, index) => ({
          name: s.name || s.schedule_name,
          type: s.type || s.reference_type,
          time: s.time || s.reference_time,
          minutesBefore: s.minutesBefore || s.minutes_before,
          displayDuration: s.displayDuration || s.display_duration || 120,
          isEnabled: (s.isEnabled !== undefined ? s.isEnabled : s.is_enabled) || 1,
          sortOrder: index
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('保存成功');
      setCleaningModalVisible(false);
      fetchCleaningSchedules();
    } catch (error) {
      console.error('保存打扫时间失败:', error);
      message.error('保存失败');
    }
  };

  // 删除打扫时间
  const handleDeleteCleaning = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v2/duty-students/cleaning-schedule/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('删除成功');
      fetchCleaningSchedules();
    } catch (error) {
      console.error('删除打扫时间失败:', error);
      message.error('删除失败');
    }
  };

  // 切换打扫时间启用状态
  const handleToggleCleaning = async (id, currentEnabled) => {
    try {
      const token = localStorage.getItem('token');
      const schedule = cleaningSchedules.find(s => s.id === id);
      
      // 更新启用状态
      const updatedSchedules = cleaningSchedules.map(s => ({
        name: s.schedule_name,
        type: s.reference_type,
        time: s.reference_time,
        minutesBefore: s.minutes_before,
        displayDuration: s.display_duration,
        isEnabled: s.id === id ? (currentEnabled === 1 ? 0 : 1) : s.is_enabled,
        sortOrder: s.sort_order
      }));

      await axios.post(`/api/v2/duty-students/cleaning-schedule/${classId}`, {
        schedules: updatedSchedules
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success(currentEnabled === 1 ? '已禁用' : '已启用');
      fetchCleaningSchedules();
    } catch (error) {
      console.error('切换状态失败:', error);
      message.error('操作失败');
    }
  };

  // 获取参考类型显示文本
  const getReferenceTypeText = (type) => {
    const typeMap = {
      'morning_reading': '早读前',
      'noon_reading': '午读前',
      'afternoon_class': '下午第一节课前',
      'after_school': '放学后',
      'fixed_time': '固定时间'
    };
    return typeMap[type] || type;
  };

  const persistWeeklySchedule = async (nextSchedule, successText) => {
    if (currentClassId.current !== classId || weeklySaveInFlight.current || weeklyLoading) return false;
    weeklySaveInFlight.current = true;
    setWeeklySaving(true);
    const requestedClassId = classId;
    const snapshot = cloneWeeklySchedule(nextSchedule);
    try {
      const token = localStorage.getItem('token');
      // 将数据转换为服务器期望的格式
      const scheduleData = [];
      
      Object.keys(snapshot).forEach(dayOfWeek => {
        snapshot[dayOfWeek].forEach(student => {
          if (student.studentId) {  // 只保存已选择学生的记录
            scheduleData.push({
              day_of_week: parseInt(dayOfWeek),
              student_id: student.studentId,
              student_name: student.studentName,
              area: student.area || null
            });
          }
        });
      });
      
      const response = await axios.post(
        `/api/v2/duty-students/weekly-schedule/${requestedClassId}`,
        { schedule: scheduleData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.data.success) throw new Error(response.data.message || '服务器未保存值日安排');
      if (currentClassId.current !== requestedClassId) return false;
      weeklyFetchVersion.current += 1;
      const savedSchedule = Object.fromEntries(Object.entries(snapshot)
        .map(([day, students]) => [day, students.filter(student => student.studentId)])
        .filter(([, students]) => students.length > 0));
      setWeeklySchedule(savedSchedule);
      message.success(successText);
      return true;
    } catch (error) {
      console.error('保存周轮值表失败:', error);
      if (currentClassId.current === requestedClassId) {
        message.error('保存失败，原安排未更改：' + (error.response?.data?.message || error.message));
      }
      return false;
    } finally {
      weeklySaveInFlight.current = false;
      setWeeklySaving(false);
    }
  };

  const openWeeklySchedule = () => {
    setWeeklyDraft(cloneWeeklySchedule(weeklySchedule));
    setWeeklyScheduleModalVisible(true);
  };

  const handleSaveWeeklySchedule = async () => {
    const save = async () => {
      if (await persistWeeklySchedule(weeklyDraft, '周轮值表保存成功')) {
        setWeeklyScheduleModalVisible(false);
      }
    };
    const hasDraftStudents = Object.values(weeklyDraft).some(students => students.some(student => student.studentId));
    if (!hasDraftStudents && Object.keys(weeklySchedule).length > 0) {
      Modal.confirm({
        title: '清空整周值日安排？',
        content: '保存后，本班整周的值日安排都会移除，需要重新安排。',
        okText: '确认清空',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: save
      });
    } else {
      await save();
    }
  };

  const handleRemoveWeeklyStudent = (day, index) => {
    const nextSchedule = cloneWeeklySchedule(weeklySchedule);
    nextSchedule[day].splice(index, 1);
    if (nextSchedule[day].length === 0) delete nextSchedule[day];
    return persistWeeklySchedule(nextSchedule, '已删除该值日安排');
  };

  const handleClearWeeklyDay = (day) => {
    const nextSchedule = cloneWeeklySchedule(weeklySchedule);
    delete nextSchedule[day];
    return persistWeeklySchedule(nextSchedule, '已清空当天值日安排');
  };

  const openWeeklyStudentEdit = (day, index) => {
    const student = weeklySchedule[day][index];
    weeklyEditForm.setFieldsValue({
      studentId: String(student.studentId),
      areas: student.area ? [student.area] : []
    });
    setEditingWeekly({ day, index });
  };

  const handleSaveWeeklyStudent = async () => {
    try {
      const values = await weeklyEditForm.validateFields();
      const student = classStudents.find(item => String(item.id) === String(values.studentId));
      if (!student) {
        message.warning('请重新选择当前班级的学生');
        return;
      }
      const nextSchedule = cloneWeeklySchedule(weeklySchedule);
      nextSchedule[editingWeekly.day][editingWeekly.index] = {
        studentId: student.id,
        studentName: student.student_name,
        area: values.areas?.[values.areas.length - 1] || null
      };
      if (await persistWeeklySchedule(nextSchedule, '值日安排已更新')) setEditingWeekly(null);
    } catch (error) {
      if (!error.errorFields) message.error('修改失败，请重试');
    }
  };

  // 下载导入模板
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v2/duty-students/weekly-schedule/template/${classId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${classInfo?.name || '班级'}_周轮值表导入模板.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      message.success('模板下载成功');
    } catch (error) {
      console.error('下载导入模板失败:', error);
      message.error('下载导入模板失败：' + (error.response?.data?.message || error.message));
    }
  };

  // 处理文件导入
  const handleFileImport = async (file) => {
    const selectedFile = file?.originFileObj || file;
    if (!selectedFile) return false;
    
    setImporting(true);
    const XLSX = require('xlsx');
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (!jsonData || jsonData.length === 0) {
            message.error('Excel文件中没有数据');
            setImporting(false);
            return;
          }
          
          // 解析数据
          const scheduleData = [];
          let successCount = 0;
          let failedCount = 0;
          const errors = [];
          
          for (const row of jsonData) {
            const dayOfWeek = normalizeDutyWeekday(
              row['星期代码（必填）'] ||
              row['星期代码'] ||
              row['星期'] ||
              row['周几']
            );
            const studentName = (row['学生姓名（必填）'] || row['学生姓名'] || '').toString().trim();
            const seatNumber = row['座位号'] ? row['座位号'].toString() : '';
            const area = row['值日区域'] || row['区域'] || null;
            
            if (!dayOfWeek || !studentName) {
              failedCount++;
              errors.push(`行数据缺少必填字段：星期代码=${dayOfWeek}, 学生姓名=${studentName}`);
              continue;
            }
            
            // 查找学生；同名时优先按座位号精确匹配，避免导入到错误学生。
            const matchedStudents = classStudents.filter(s => s.student_name === studentName);
            const student = seatNumber
              ? matchedStudents.find(s => String(s.seat_number || '') === seatNumber)
              : matchedStudents.length === 1 ? matchedStudents[0] : null;
            
            if (!student) {
              failedCount++;
              errors.push(`找不到学生：${studentName}${seatNumber ? `(座位号${seatNumber})` : ''}`);
              continue;
            }
            
            scheduleData.push({
              day_of_week: parseInt(dayOfWeek),
              student_id: student.id,
              student_name: student.student_name,
              area: area
            });
            successCount++;
          }
          
          if (scheduleData.length === 0) {
            message.error('没有可导入的数据');
            setImporting(false);
            return;
          }
          
          // 保存到服务器
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `/api/v2/duty-students/weekly-schedule/${classId}`,
            { schedule: scheduleData },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            message.success(`导入成功：${successCount}条，失败：${failedCount}条`);
            if (errors.length > 0 && errors.length <= 5) {
              message.warning('失败原因：' + errors.join('; '));
            }
            setImportModalVisible(false);
            fetchWeeklySchedule();  // 重新加载数据
          }
        } catch (error) {
          console.error('解析Excel文件失败:', error);
          message.error('解析Excel文件失败：' + error.message);
        } finally {
          setImporting(false);
        }
      };
      
      reader.onerror = () => {
        message.error('读取文件失败');
        setImporting(false);
      };
      
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      console.error('处理文件失败:', error);
      message.error('处理文件失败：' + error.message);
      setImporting(false);
    }
    
    return false; // 阻止自动上传
  };

  // 保存周轮值表（旧函数保留）
  const handleSaveWeeklyScheduleOld = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // 转换为后端格式
      const scheduleData = [];
      Object.keys(weeklySchedule).forEach(dayOfWeek => {
        weeklySchedule[dayOfWeek].forEach(item => {
          scheduleData.push({
            dayOfWeek: parseInt(dayOfWeek),
            studentId: item.studentId,
            studentName: item.studentName,
            dutyArea: item.area
          });
        });
      });

      await axios.post('/api/v2/duty-students/weekly-schedule', {
        classId,
        schedule: scheduleData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('周轮值表保存成功');
      setWeeklyScheduleModalVisible(false);
      fetchWeeklySchedule();
    } catch (error) {
      console.error('保存周轮值表失败:', error);
      message.error('保存周轮值表失败：' + (error.response?.data?.message || error.message));
    }
  };

  // 一键生成周轮值表
  const handleAutoArrange = async () => {
    try {
      const values = await autoArrangeForm.validateFields();
      
      if (classStudents.length === 0) {
        message.warning('班级暂无学生，无法生成值日表');
        return;
      }

      const { studentsPerDay, includeWeekend, autoAssignArea } = values;
      
      // 确定要分配的天数
      const daysToArrange = includeWeekend ? [1, 2, 3, 4, 5, 6, 7] : [1, 2, 3, 4, 5];
      
      // 随机打乱学生顺序
      const shuffledStudents = [...classStudents].sort(() => Math.random() - 0.5);
      
      // 计算每天需要的学生数
      const totalSlots = daysToArrange.length * studentsPerDay;
      
      // 如果学生不够，需要重复使用
      const studentsToUse = [];
      while (studentsToUse.length < totalSlots) {
        studentsToUse.push(...shuffledStudents);
      }
      
      // 分配到各天
      const newSchedule = {};
      let studentIndex = 0;
      
      daysToArrange.forEach(day => {
        newSchedule[day] = [];
        for (let i = 0; i < studentsPerDay; i++) {
          if (studentIndex < studentsToUse.length) {
            const student = studentsToUse[studentIndex];
            
            // 自动分配区域
            let area = null;
            if (autoAssignArea) {
              const availableAreas = getAvailableAreas();
              const areaIndex = i % availableAreas.length;
              area = availableAreas[areaIndex].value;
            }
            
            newSchedule[day].push({
              studentId: student.id,
              studentName: student.student_name,
              area: area
            });
            
            studentIndex++;
          }
        }
      });
      
      setWeeklyDraft(newSchedule);
      message.success(`已自动生成${daysToArrange.length}天的值日安排，共分配${studentIndex}个值日岗位`);
      setAutoArrangeModalVisible(false);
      setWeeklyScheduleModalVisible(true); // 打开周轮值表查看
      
    } catch (error) {
      console.error('自动生成失败:', error);
      message.error('生成失败');
    }
  };

  // 打开添加值日生对话框
  const openModal = () => {
    setSelectedStudents([]);
    setStudentAreas({});
    setSelectedDate(moment());
    setModalVisible(true);
  };

  // 处理学生选择变化
  const handleStudentChange = (checkedValues) => {
    setSelectedStudents(checkedValues);
    // 移除取消选择的学生的区域分配
    const newAreas = { ...studentAreas };
    Object.keys(newAreas).forEach(key => {
      if (!checkedValues.includes(parseInt(key))) {
        delete newAreas[key];
      }
    });
    setStudentAreas(newAreas);
  };

  // 设置学生的值日区域
  const handleAreaChange = (studentId, area) => {
    setStudentAreas(prev => ({
      ...prev,
      [studentId]: area
    }));
  };

  // 批量添加值日生
  const handleSaveDutyStudents = async () => {
    if (selectedStudents.length === 0) {
      message.warning('请至少选择一名学生');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const students = selectedStudents.map(studentId => {
        const student = classStudents.find(s => s.id === studentId);
        return {
          studentId: student.id,
          studentName: student.student_name,
          studentNumber: student.seat_number || student.student_id,
          dutyArea: studentAreas[studentId] || null // 添加值日区域
        };
      });

      await axios.post('/api/v2/duty-students/batch', {
        classId,
        dutyDate: selectedDate.format('YYYY-MM-DD'),
        students
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('值日生安排成功');
      setModalVisible(false);
      fetchDutyStudents();
    } catch (error) {
      console.error('添加值日生失败:', error);
      message.error('添加值日生失败');
    }
  };

  // 删除值日生记录
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v2/duty-students/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('删除成功');
      fetchDutyStudents();
    } catch (error) {
      console.error('删除值日生失败:', error);
      message.error('删除失败');
    }
  };

  // 标记完成/未完成
  const handleToggleComplete = async (record) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/v2/duty-students/${record.id}`, {
        isCompleted: !record.is_completed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('状态更新成功');
      fetchDutyStudents();
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 保存展示配置
  const handleSaveConfig = async (values) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/v2/duty-students/display-config', {
        classId,
        displayBeforeMorningReading: values.displayBeforeMorningReading,
        morningReadingTime: values.morningReadingTime.format('HH:mm:ss'),
        displayBeforeNoonReading: values.displayBeforeNoonReading,
        noonReadingTime: values.noonReadingTime.format('HH:mm:ss'),
        displayDuration: values.displayDuration || 300
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('配置保存成功');
      setConfigModalVisible(false);
      fetchDisplayConfig();
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    }
  };

  // 打开配置对话框
  const openConfigModal = () => {
    if (displayConfig) {
      configForm.setFieldsValue({
        displayBeforeMorningReading: displayConfig.display_before_morning_reading || displayConfig.displayBeforeMorningReading,
        morningReadingTime: moment(displayConfig.morning_reading_time || displayConfig.morningReadingTime, 'HH:mm:ss'),
        displayBeforeNoonReading: displayConfig.display_before_noon_reading || displayConfig.displayBeforeNoonReading,
        noonReadingTime: moment(displayConfig.noon_reading_time || displayConfig.noonReadingTime, 'HH:mm:ss'),
        displayDuration: displayConfig.display_duration || displayConfig.displayDuration || 300
      });
    }
    setConfigModalVisible(true);
  };

  const columns = [
    {
      title: '值日日期',
      dataIndex: 'duty_date',
      key: 'duty_date',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name'
    },
    {
      title: '学号/座位号',
      dataIndex: 'student_number',
      key: 'student_number'
    },
    {
      title: '值日区域',
      dataIndex: 'duty_area',
      key: 'duty_area',
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'is_completed',
      key: 'is_completed',
      render: (completed, record) => (
        <Tag 
          color={completed ? 'green' : 'orange'}
          style={{ cursor: 'pointer' }}
          onClick={() => handleToggleComplete(record)}
        >
          {completed ? '已完成' : '未完成'}
        </Tag>
      )
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="确定要删除这条记录吗?"
          onConfirm={() => handleDelete(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div style={{ padding: '20px 0' }}>
      {/* 内部Tab切换 */}
      <Tabs activeKey={innerTab} onChange={setInnerTab} style={{ marginBottom: 16 }}>
        <TabPane tab="值日生列表" key="list">
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
            <Space wrap>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openModal}
          >
            安排值日生
          </Button>
          <Button 
            type={viewMode === 'weekly' ? 'primary' : 'default'}
            icon={<CalendarOutlined />}
            onClick={openWeeklySchedule}
            disabled={weeklyBusy}
          >
            周轮值表
          </Button>
          <Button 
            icon={<UploadOutlined />}
            onClick={() => setImportModalVisible(true)}
            disabled={weeklyBusy}
          >
            导入周轮值表
          </Button>
          <Button 
            type="primary"
            ghost
            icon={<UserAddOutlined />}
            onClick={() => setAutoArrangeModalVisible(true)}
            disabled={weeklyBusy}
          >
            一键生成
          </Button>
          <Button 
            icon={<SettingOutlined />}
            onClick={openConfigModal}
          >
            展示配置
          </Button>
          {viewMode === 'weekly' && (
            <Popconfirm
              title="清空整周值日安排？"
              description="只清空每周固定安排，按日期安排的记录不受影响。"
              onConfirm={() => persistWeeklySchedule({}, '已清空整周值日安排')}
              okText="确认清空"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              disabled={weeklyBusy || Object.keys(weeklySchedule).length === 0}
            >
              <Button danger icon={<DeleteOutlined />} disabled={weeklyBusy || Object.keys(weeklySchedule).length === 0}>
                清空整周
              </Button>
            </Popconfirm>
          )}
        </Space>
        
        <Space>
          <Button 
            type={viewMode === 'date' ? 'primary' : 'default'}
            onClick={() => setViewMode('date')}
          >
            按日期查看
          </Button>
          <Button 
            type={viewMode === 'weekly' ? 'primary' : 'default'}
            onClick={() => setViewMode('weekly')}
          >
            按星期查看
          </Button>
        </Space>
      </div>

      {viewMode === 'date' ? (
        // 按日期显示
        <Card>
          <Table
            columns={columns}
            dataSource={dutyStudents}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
          />
        </Card>
      ) : (
        // 按星期显示
        <div>
          {Object.keys(weeklySchedule).length === 0 ? (
            <Alert
              message="暂无周轮值表"
              description='请点击"周轮值表"按钮配置每周固定的值日生安排'
              type="info"
              showIcon
            />
          ) : (
            weekDays.map(day => {
              const dayStudents = weeklySchedule[day.value] || [];
              if (dayStudents.length === 0) return null;
              
              return (
                <Card 
                  key={day.value}
                  title={<span style={{ fontSize: 16, fontWeight: 600 }}>{day.label} <span style={{ fontSize: 12, fontWeight: 400, color: '#666' }}>（{dayStudents.length}项安排）</span></span>}
                  extra={
                    <Popconfirm
                      title={`清空${day.label}值日安排？`}
                      description="其他星期的安排会保留。"
                      onConfirm={() => handleClearWeeklyDay(day.value)}
                      okText="确认清空"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      disabled={weeklyBusy}
                    >
                      <Button danger size="small" aria-label={`清空${day.label}值日安排`} disabled={weeklyBusy}>
                        清空当天
                      </Button>
                    </Popconfirm>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {dayStudents.map((student, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#f0f7ff',
                          borderRadius: 6,
                          border: '1px solid #b3d8ff',
                          minWidth: 160
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                          {student.studentName}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {student.area || '未分配区域'}
                        </div>
                        <Space style={{ marginTop: 8 }}>
                          <Button
                            type="link" size="small" icon={<EditOutlined />}
                            style={{ padding: 0 }}
                            aria-label={`编辑${day.label}${student.studentName}的${student.area || '值日'}安排`}
                            onClick={() => openWeeklyStudentEdit(day.value, index)}
                            disabled={weeklyBusy}
                          >
                            编辑
                          </Button>
                          <Popconfirm
                            title={`删除${student.studentName}的这项值日安排？`}
                            description={`${day.label} · ${student.area || '未分配区域'}，其他安排会保留。`}
                            onConfirm={() => handleRemoveWeeklyStudent(day.value, index)}
                            okText="确认删除" cancelText="取消"
                            okButtonProps={{ danger: true }} disabled={weeklyBusy}
                          >
                            <Button
                              type="link" danger size="small" icon={<DeleteOutlined />}
                              style={{ padding: 0 }}
                              aria-label={`删除${day.label}${student.studentName}的${student.area || '值日'}安排`}
                              disabled={weeklyBusy}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      <Modal
        title="修改值日安排"
        open={Boolean(editingWeekly)}
        onCancel={() => { if (!weeklySaving) setEditingWeekly(null); }}
        onOk={handleSaveWeeklyStudent}
        okText="保存修改" cancelText="取消"
        confirmLoading={weeklySaving}
        cancelButtonProps={{ disabled: weeklySaving }}
        closable={!weeklySaving} maskClosable={false} keyboard={!weeklySaving}
        forceRender
      >
        <Form form={weeklyEditForm} layout="vertical">
          <Form.Item label="值日学生" name="studentId" rules={[{ required: true, message: '请选择值日学生' }]}>
            <Select showSearch optionFilterProp="label" disabled={weeklySaving}
              options={classStudents.map(student => ({
                value: String(student.id),
                label: `${student.student_name}${student.seat_number ? `（${student.seat_number}号）` : ''}`
              }))}
            />
          </Form.Item>
          <Form.Item label="负责区域" name="areas" normalize={areas => areas?.slice(-1) || []}>
            <Select mode="tags" maxTagCount={1} allowClear disabled={weeklySaving}
              placeholder="选择或输入值日区域" options={getAvailableAreas()}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加值日生对话框 */}
      <Modal
        title="安排值日生"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSaveDutyStudents}
        width={800}
      >
        <Form layout="vertical">
          <Form.Item label="值日日期">
            <DatePicker 
              value={selectedDate}
              onChange={setSelectedDate}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="选择学生并分配值日区域">
            <Checkbox.Group
              style={{ width: '100%' }}
              value={selectedStudents}
              onChange={handleStudentChange}
            >
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {classStudents.map(student => (
                  <div 
                    key={student.id} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: 12,
                      padding: '8px 12px',
                      backgroundColor: selectedStudents.includes(student.id) ? '#f0f7ff' : '#fafafa',
                      borderRadius: 4,
                      border: selectedStudents.includes(student.id) ? '1px solid #1890ff' : '1px solid #d9d9d9'
                    }}
                  >
                    <Checkbox value={student.id} style={{ marginRight: 12 }}>
                      <span style={{ fontWeight: 500 }}>
                        {student.student_name} 
                        {student.seat_number && <span style={{ color: '#999', marginLeft: 4 }}>({student.seat_number})</span>}
                      </span>
                    </Checkbox>
                    
                    {selectedStudents.includes(student.id) && (
                      <Select
                        mode="tags"
                        placeholder="输入或选择值日区域"
                        style={{ flex: 1, marginLeft: 'auto', minWidth: 200 }}
                        value={studentAreas[student.id] ? [studentAreas[student.id]] : []}
                        onChange={(values) => handleAreaChange(student.id, values[values.length - 1] || null)}
                        allowClear
                        showSearch
                        maxTagCount={1}
                      >
                        {getAvailableAreas().map(area => (
                          <Option key={area.value} value={area.value}>
                            {area.label}
                            {area.description && (
                              <div style={{ fontSize: 12, color: '#999' }}>
                                {area.description}
                              </div>
                            )}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <div style={{ marginTop: 16, color: '#999', fontSize: 12, backgroundColor: '#e6f7ff', padding: 12, borderRadius: 4 }}>
            <div style={{ fontWeight: 500, marginBottom: 4, color: '#0050b3' }}>💡 提示：</div>
            <div>1. 勾选学生后，可为每个学生分配具体的值日区域</div>
            <div>2. 值日区域可选，也可以自定义输入</div>
            <div>3. 已选择 {selectedStudents.length} 名学生</div>
          </div>
        </Form>
      </Modal>

      {/* 展示配置对话框 */}
      <Modal
        title="值日生展示配置"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={() => configForm.submit()}
        width={500}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSaveConfig}
        >
          <Form.Item
            label="早读前展示"
            name="displayBeforeMorningReading"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>启用早读前展示值日生</Checkbox>
          </Form.Item>

          <Form.Item
            label="早读时间"
            name="morningReadingTime"
            initialValue={moment('06:30:00', 'HH:mm:ss')}
          >
            <DatePicker.TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="午读前展示"
            name="displayBeforeNoonReading"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>启用午读前展示值日生</Checkbox>
          </Form.Item>

          <Form.Item
            label="午读时间"
            name="noonReadingTime"
            initialValue={moment('12:30:00', 'HH:mm:ss')}
          >
            <DatePicker.TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="展示时长（秒）"
            name="displayDuration"
            initialValue={300}
          >
            <Input type="number" min={60} max={600} />
          </Form.Item>

          <div style={{ color: '#999', fontSize: 12 }}>
            提示：桌面客户端将在设置的时间前展示当天的值日生名单
          </div>
        </Form>
      </Modal>

      {/* 导入周轮值表模态框 */}
      <Modal
        title="导入周轮值表"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={[
          <Button key="template" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            下载导入模板
          </Button>,
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            取消
          </Button>
        ]}
        width={600}
      >
        <Alert
          message="导入说明"
          description={
            <div>
              <p>1. 点击"下载导入模板"获取Excel模板</p>
              <p>2. 按照模板格式填写数据（星期代码和学生姓名为必填项）</p>
              <p>3. 学生姓名必须与班级中的学生姓名完全一致</p>
              <p>4. 上传填好的Excel文件进行导入</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Upload
          accept=".xlsx,.xls"
          beforeUpload={handleFileImport}
          showUploadList={false}
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={importing}
            block
            size="large"
            type="primary"
          >
            {importing ? '导入中...' : '选择Excel文件上传'}
          </Button>
        </Upload>

        <div style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
          提示：如果班级中有学生看不到，请先在"学生管理"中导入学生数据，并确保学生已分配到当前班级
        </div>
      </Modal>

      {/* 周轮值表配置对话框 */}
      <Modal
        title="配置周轮值表"
        open={weeklyScheduleModalVisible}
        onCancel={() => setWeeklyScheduleModalVisible(false)}
        onOk={handleSaveWeeklySchedule}
        okText="保存安排" cancelText="取消"
        confirmLoading={weeklySaving}
        cancelButtonProps={{ disabled: weeklySaving }}
        closable={!weeklySaving} maskClosable={false} keyboard={!weeklySaving}
        width={900}
        style={{ top: 20 }}
      >
        <Alert
          message="说明"
          description='为每个星期分配固定的值日生及其负责区域。配置后，可在"按星期查看"中查看完整的周轮值表。'
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Tabs tabPosition="left">
          {weekDays.map(day => (
            <TabPane tab={day.label} key={day.value}>
              <div style={{ padding: '0 16px' }}>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const newSchedule = cloneWeeklySchedule(weeklyDraft);
                      if (!newSchedule[day.value]) {
                        newSchedule[day.value] = [];
                      }
                      newSchedule[day.value].push({
                        studentId: null,
                        studentName: '',
                        area: ''
                      });
                      setWeeklyDraft(newSchedule);
                    }}
                  >
                    添加值日生
                  </Button>
                </div>

                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {(weeklyDraft[day.value] || []).map((student, index) => {
                    // 过滤掉已选中的学生（除了当前这个）
                    const selectedStudentIds = (weeklyDraft[day.value] || [])
                      .map((s, i) => i !== index ? s.studentId : null)
                      .filter(id => id !== null);
                    const availableStudents = classStudents.filter(s => !selectedStudentIds.includes(s.id));
                    
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: 12,
                          marginBottom: 12,
                          padding: 12,
                          backgroundColor: '#fafafa',
                          borderRadius: 6,
                          alignItems: 'center'
                        }}
                      >
                        <Select
                          placeholder="选择学生"
                          style={{ flex: 1 }}
                          value={student.studentId}
                          onChange={(value) => {
                            const selectedStudent = classStudents.find(s => s.id === value);
                            const newSchedule = cloneWeeklySchedule(weeklyDraft);
                            newSchedule[day.value][index] = {
                              ...newSchedule[day.value][index],
                              studentId: value,
                              studentName: selectedStudent?.student_name || ''
                            };
                            setWeeklyDraft(newSchedule);
                          }}
                          showSearch
                          filterOption={(input, option) => {
                            if (!input) return true;
                            const children = option.children || '';
                            return children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                          }}
                        >
                          {availableStudents.map(s => (
                            <Option key={s.id} value={s.id}>
                              {s.student_name} {s.seat_number && `(${s.seat_number})`}
                            </Option>
                          ))}
                        </Select>

                        <Select
                          mode="tags"
                          placeholder="输入或选择值日区域"
                          style={{ flex: 1 }}
                          value={student.area ? [student.area] : []}
                          onChange={(values) => {
                            const newSchedule = cloneWeeklySchedule(weeklyDraft);
                            newSchedule[day.value][index].area = values[values.length - 1] || null;
                            setWeeklyDraft(newSchedule);
                          }}
                          allowClear
                          showSearch
                          maxTagCount={1}
                        >
                          {getAvailableAreas().map(area => (
                            <Option key={area.value} value={area.value}>
                              {area.label}
                              {area.description && (
                                <div style={{ fontSize: 12, color: '#999' }}>
                                  {area.description}
                                </div>
                              )}
                            </Option>
                          ))}
                        </Select>

                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const newSchedule = cloneWeeklySchedule(weeklyDraft);
                            newSchedule[day.value].splice(index, 1);
                            if (newSchedule[day.value].length === 0) {
                              delete newSchedule[day.value];
                            }
                            setWeeklyDraft(newSchedule);
                          }}
                        >
                          删除
                        </Button>
                      </div>
                    );
                  })}
                  
                  {(!weeklyDraft[day.value] || weeklyDraft[day.value].length === 0) && (
                    <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                      暂无值日生，点击"添加值日生"按钮开始配置
                    </div>
                  )}
                </div>
              </div>
            </TabPane>
          ))}
        </Tabs>
      </Modal>

      {/* 一键生成值日表模态框 */}
      <Modal
        title="一键生成周轮值表"
        open={autoArrangeModalVisible}
        onCancel={() => setAutoArrangeModalVisible(false)}
        onOk={handleAutoArrange}
        width={600}
      >
        <Alert
          message="系统将自动为您分配值日生"
          description={`班级共有 ${classStudents.length} 名学生，系统将随机分配到各天的值日岗位`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={autoArrangeForm}
          layout="vertical"
          initialValues={{
            studentsPerDay: 4,
            includeWeekend: false,
            autoAssignArea: true
          }}
        >
          <Form.Item
            label="每天值日生人数"
            name="studentsPerDay"
            rules={[{ required: true, message: '请输入每天值日生人数' }]}
          >
            <InputNumber 
              min={1} 
              max={20} 
              style={{ width: '100%' }}
              placeholder="建议4-6人"
            />
          </Form.Item>

          <Form.Item
            label="包含周末"
            name="includeWeekend"
            valuePropName="checked"
          >
            <Checkbox>是否包含周六、周日</Checkbox>
          </Form.Item>

          <Form.Item
            label="自动分配区域"
            name="autoAssignArea"
            valuePropName="checked"
          >
            <Checkbox>自动为每个值日生分配区域（扫地、拖地、擦黑板等）</Checkbox>
          </Form.Item>

          <Alert
            message="说明"
            description={
              <div>
                <div>• 系统将随机打乱学生顺序，均匀分配到各天</div>
                <div>• 如果学生数不足，部分学生会值日多次</div>
                <div>• 生成后可在周轮值表中查看和编辑</div>
              </div>
            }
            type="warning"
            showIcon
          />
        </Form>
      </Modal>
        </TabPane>

        {/* 区域设置Tab */}
        <TabPane 
          tab={
            <span>
              <AppstoreOutlined />
              区域设置 {customAreas.length > 0 && `(${customAreas.length})`}
            </span>
          } 
          key="areas"
        >
          <DutyAreaSettings
            classId={classId}
            onSuccess={fetchCustomAreas}
          />
        </TabPane>

        {/* 打扫时间设置Tab */}
        <TabPane
          tab={
            <span>
              <ClockCircleOutlined />
              打扫时间设置 {cleaningSchedules.length > 0 && `(${cleaningSchedules.length})`}
            </span>
          }
          key="cleaning"
        >
          <div style={{ maxWidth: 1200 }}>
            {/* 模板选择区域 */}
            <Card 
              title="选择打扫时间模板" 
              size="small" 
              style={{ marginBottom: 16 }}
              extra={
                selectedTemplate && selectedTemplate !== 'custom' ? (
                  <Button type="primary" onClick={handleApplyTemplate}>
                    应用选中的模板
                  </Button>
                ) : null
              }
            >
              <div>
                <p style={{ color: '#666', marginBottom: 12 }}>
                  选择一个适合的打扫时间模板快速设置，或自定义打扫时间：
                </p>
                <Radio.Group
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {/* 四个选项横向排列 */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {cleaningTemplates.map(template => (
                      <Card
                        key={template.id}
                        size="small"
                        hoverable
                        style={{
                          flex: 1,
                          cursor: 'pointer',
                          border: selectedTemplate === template.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                          backgroundColor: selectedTemplate === template.id ? '#e6f7ff' : 'white'
                        }}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Radio value={template.id} style={{ marginBottom: 8 }}>
                            <strong>{template.name}</strong>
                          </Radio>
                          <div style={{ fontSize: 12, color: '#999', minHeight: 32 }}>
                            {template.description}
                          </div>
                        </div>
                      </Card>
                    ))}
                    
                    {/* 自定义选项 */}
                    <Card
                      size="small"
                      hoverable
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        border: selectedTemplate === 'custom' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                        backgroundColor: selectedTemplate === 'custom' ? '#e6f7ff' : 'white'
                      }}
                      onClick={() => setSelectedTemplate('custom')}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <Radio value="custom" style={{ marginBottom: 8 }}>
                          <strong>自定义配置</strong>
                        </Radio>
                        <div style={{ fontSize: 12, color: '#999', minHeight: 32 }}>
                          手动添加配置每个打扫时段
                        </div>
                      </div>
                    </Card>
                  </div>
                </Radio.Group>
                
                {/* 提示信息 */}
                {selectedTemplate && selectedTemplate !== 'custom' && (
                  <Alert
                    message="⚠️ 应用模板将替换现有的打扫时间安排"
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                )}
                
                {selectedTemplate === 'custom' && (
                  <Alert
                    message="自定义模式"
                    description='您可以在下方"当前打扫时间安排"中点击"添加打扫时间"按钮，手动添加和配置每个打扫时段。'
                    type="info"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                )}
              </div>
            </Card>

            {/* 当前打扫时间安排 */}
            <Card 
              title="当前打扫时间安排" 
              size="small"
              extra={
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => openCleaningModal()}
                >
                  添加打扫时间
                </Button>
              }
            >
              {cleaningSchedules.length === 0 ? (
                <Alert
                  message="暂无打扫时间安排"
                  description="请选择模板快速配置，或手动添加打扫时间"
                  type="info"
                  showIcon
                />
              ) : (
                <Table
                  dataSource={cleaningSchedules}
                  rowKey="id"
                  pagination={false}
                  size="small"
                >
                  <Table.Column
                    title="序号"
                    dataIndex="sort_order"
                    width={60}
                    render={(value, record, index) => index + 1}
                  />
                  <Table.Column
                    title="时段名称"
                    dataIndex="schedule_name"
                    width={150}
                  />
                  <Table.Column
                    title="参考类型"
                    dataIndex="reference_type"
                    width={120}
                    render={(value) => getReferenceTypeText(value)}
                  />
                  <Table.Column
                    title="参考时间"
                    dataIndex="reference_time"
                    width={120}
                    render={(value, record) => {
                      // 如果手动设置了时间，优先显示手动设置的时间
                      if (value) {
                        return <span style={{ color: '#52c41a' }}>{moment(value, 'HH:mm:ss').format('HH:mm')}</span>;
                      }
                      
                      // 如果是固定时间类型但没有值
                      if (record.reference_type === 'fixed_time') {
                        return '-';
                      }
                      
                      // 显示从作息时间表获取的实际时间
                      const timeText = getReferenceTime(record.reference_type);
                      const isError = timeText === '作息时间未设置' || timeText === '未设置';
                      
                      return (
                        <span style={{ color: isError ? '#ff4d4f' : '#1890ff' }}>
                          {timeText}
                        </span>
                      );
                    }}
                  />
                  <Table.Column
                    title="提前分钟"
                    dataIndex="minutes_before"
                    width={100}
                    render={(value) => `${value} 分钟`}
                  />
                  <Table.Column
                    title="显示时长"
                    dataIndex="display_duration"
                    width={100}
                    render={(value) => `${value} 秒`}
                  />
                  <Table.Column
                    title="状态"
                    dataIndex="is_enabled"
                    width={80}
                    render={(value, record) => (
                      <Switch
                        checked={value === 1}
                        onChange={() => handleToggleCleaning(record.id, value)}
                        checkedChildren="启用"
                        unCheckedChildren="禁用"
                      />
                    )}
                  />
                  <Table.Column
                    title="操作"
                    width={120}
                    render={(record) => (
                      <Space>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => openCleaningModal(record)}
                        >
                          编辑
                        </Button>
                        <Popconfirm
                          title="确定要删除这个打扫时间吗？"
                          onConfirm={() => handleDeleteCleaning(record.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button type="link" size="small" danger>
                            删除
                          </Button>
                        </Popconfirm>
                      </Space>
                    )}
                  />
                </Table>
              )}

              <Alert
                message="使用说明"
                description={
                  <>
                    <p>• <strong>参考类型</strong>：选择打扫时间的参考点（早读前、午读前、下午上课前、固定时间）</p>
                    <p>• <strong>提前分钟</strong>：在参考时间前多少分钟开始显示值日生提醒</p>
                    <p>• <strong>显示时长</strong>：值日生提醒显示多少秒后自动切换到课程表</p>
                    <p>• 桌面端会在设定的时间自动循环显示值日生名单和课程表，提醒学生进行卫生值日</p>
                  </>
                }
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            </Card>
          </div>

          {/* 添加/编辑打扫时间Modal */}
          <Modal
            title={editingCleaning ? '编辑打扫时间' : '添加打扫时间'}
            open={cleaningModalVisible}
            onOk={handleSaveCleaning}
            onCancel={() => setCleaningModalVisible(false)}
            width={600}
          >
            <Form
              form={cleaningForm}
              layout="vertical"
              initialValues={{
                minutesBefore: 10,
                displayDuration: 120,
                isEnabled: true
              }}
            >
              <Form.Item
                label="时段名称"
                name="name"
                rules={[{ required: true, message: '请输入时段名称' }]}
              >
                <Input placeholder="如：早读前打扫、下午上课前打扫" />
              </Form.Item>

              <Form.Item
                label="参考类型"
                name="type"
                rules={[{ required: true, message: '请选择参考类型' }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="morning_reading">早读前（自动获取早读时间或上午第一节课）</Radio>
                    <Radio value="noon_reading">午读前（自动获取午读时间或下午第一节课）</Radio>
                    <Radio value="afternoon_class">下午第一节课前（自动获取下午第一节课时间）</Radio>
                    <Radio value="after_school">放学后（自动获取最后一节课结束时间）</Radio>
                    <Radio value="fixed_time">固定时间</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label="手动指定时间（可选）"
                name="time"
                tooltip="如果指定了时间，将覆盖自动获取的时间"
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="留空则自动获取" />
              </Form.Item>

              <Form.Item
                label="提前多少分钟显示"
                name="minutesBefore"
                rules={[{ required: true, message: '请输入提前分钟数' }]}
              >
                <InputNumber min={0} max={60} style={{ width: '100%' }} addonAfter="分钟" />
              </Form.Item>

              <Form.Item
                label="显示时长"
                name="displayDuration"
                rules={[{ required: true, message: '请输入显示时长' }]}
                tooltip="值日生提醒在桌面端显示多少秒后切换到课程表"
              >
                <InputNumber min={10} max={120} style={{ width: '100%' }} addonAfter="秒" />
              </Form.Item>

              <Form.Item
                label="启用状态"
                name="isEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default DutyStudentManagement;

